import { Component, Input, OnInit, ElementRef, OnDestroy } from '@angular/core';

import { Observable, Subscription, combineLatest, from, of } from 'rxjs';
import { map, tap, switchMap, catchError, filter } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';

import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';

import { PreviewTextDialogComponent } from '@Common/Dialogs/components/PreviewTextDialog/preview-text-dialog.component';
import { DistributionApi } from '@api/distributions/distribution.api';
import {
    PersonalizationListApi,
    PersonalizationValue,
    PersonalizationSelection,
    PatchPersonalization,
    PersonalizationSummary
} from '@api/distributions/model/distribution-personalization.api.model';
import { PersonalizationSourceTypeEnum } from '@api/distributions/enum/personalization-source-type.enum';

import { PersonalizationTagEnum } from '@Discover/Targeting/enums/personalization-tag.enum';
import { OutletModel } from '@Discover/Targeting/models/outlet.model';
import { ContactModel } from '@Common/TargetingShared/model/contact.model';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { TargetingRhpContextsService } from '@Common/TargetingShared/services/targeting-rhp-contexts.service';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { Personalization } from '@Discover/Targeting/models/personalization.model';
import { PersonalizationService } from '@Discover/Targeting/services/personalization.service';
import { ContactsPublicApi } from '@api/targeting/contacts-public.api';
import { OutletsPublicApi } from '@api/targeting/outlets-public.api';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { FallbackPersonalizationsDialogComponent } from '@Common/Dialogs/components/FallbackPersonalizationsDialog/fallback-personalizations-dialog.component';
import { PersonalizationWorkbenchDialogComponent } from '../PersonalizationWorkbenchDialog/personalization-workbench-dialog.component';
import { PageCriteriaModelInterface } from '@Common/Misc/models/page-criteria.model.interface';
import { ResourceBodyApiModel } from '@api/distributions/model/distribution-body.api.model';
import { DistributionModel } from '@Discover/Targeting/models/distribution.model';
import { HttpErrorResponse } from '@angular/common/http';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';

export enum RecipientPersonalizationTab {
    edit = 0,
    preview = 1
}

@Component({
    selector: 'agpr-recipient-personalization-dialog',
    templateUrl: './recipient-personalization-dialog.component.html',
    styleUrls: ['./recipient-personalization-dialog.component.scss'],
    providers: [],
    standalone: false
})
export class RecipientPersonalizationDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() selectedTab: RecipientPersonalizationTab = RecipientPersonalizationTab.edit;
    @Input() currentEntity: ContactModel | OutletModel;
    @Input() distributionId: number;
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    personalizations: Personalization[] = [];
    hideSlideOverlay: boolean = false;
    loading: boolean = true;

    highlightTags: boolean = true;

    unsavedPeronsalizations: number = 0;
    unsavedPeronsalizationTypes: PersonalizationTagEnum[] = [];
    distHeadingIndex: number = -1;
    contactHeadingIndex: number = -1;


    recipients: (ContactModel | OutletModel)[] = [];
    pageCriteria: PageCriteriaModelInterface;
    setPageBeginning: boolean = true;
    currentIndex: number = 0;
    showAiHelper: boolean = false;

    // cleaned html, replaced with personalization tag values
    body: string = '';
    subject: string = '';
    preview: string = '';

    // cleaned html, replaced with highlighted personalization tag values
    bodyHighlight: string = '';
    subjectHighlight: string = '';
    previewHighlight: string = '';

    // original html, with personalization template tags eg. [%FNAME%]
    private bodyHtml: string = '';
    private subjectHtml: string = '';
    private previewHtml: string = '';

    private detailSummary: PersonalizationSummary[] = [];

    readonly id = 'recipient_personalization_dialog';
    readonly buttonType = ButtonTypesEnum;
    readonly personalizationTagEnum = PersonalizationTagEnum;
    readonly personalizationSourceTypeEnum = PersonalizationSourceTypeEnum;
    readonly MAX_TEXT_COUNT: number = 110;
    readonly entityType = EntityType;
    readonly personalizationOrder = [
        PersonalizationTagEnum.PREFIX,
        PersonalizationTagEnum.FNAME,
        PersonalizationTagEnum.LNAME,
        PersonalizationTagEnum.OUTLET_NAME,
        PersonalizationTagEnum.X_HANDLE,
        PersonalizationTagEnum.INTRO,
        PersonalizationTagEnum.OUTRO
    ];
    readonly personalizationContactTypes = [
        PersonalizationTagEnum.PREFIX,
        PersonalizationTagEnum.FNAME,
        PersonalizationTagEnum.LNAME,
        PersonalizationTagEnum.OUTLET_NAME,
        PersonalizationTagEnum.X_HANDLE,
    ];
    readonly allowedContactProfileSaveTags = [
        PersonalizationTagEnum.PREFIX,
        PersonalizationTagEnum.FNAME,
        PersonalizationTagEnum.LNAME,
        PersonalizationTagEnum.OUTLET_NAME
    ];
    readonly allowedOutletProfileSaveTags = [
        PersonalizationTagEnum.OUTLET_NAME
    ];

    private subscriptions: Subscription[] = [];

    constructor(
        coreDialogService: CoreDialogService,
        public personalizationService: PersonalizationService,
        private distributionApi: DistributionApi,
        private distributionService: DistributionService,
        private targetingRhpContextsService: TargetingRhpContextsService,
        private localizationService: LocalizationService,
        private contactsPublicApi: ContactsPublicApi,
        private outletsPublicApi: OutletsPublicApi,
        private messageService: MessageService,
        public el: ElementRef,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super(coreDialogService, 'RecipientPersonalizationDialog');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.loadCurrentEntity().subscribe(),
            this.personalizationService.getRecipientPersonalizations().pipe(
                map(recipientPersonalizations => recipientPersonalizations.map(x => x.recipient)),
                switchMap(recipients => {
                    this.recipients = recipients;
                    this.currentIndex = recipients.findIndex(x => x.id === this.currentEntity?.id);
                    if (!this.currentEntity && this.recipients.length > 0) {
                        this.currentIndex = (this.setPageBeginning) ? 0 : this.recipients.length - 1;
                        this.currentEntity = this.recipients[this.currentIndex];

                        return this.loadCurrentEntity();
                    }

                    return of(null);
                })
            ).subscribe(),
            this.personalizationService.getPageCriteria().subscribe(pageData => {
                this.pageCriteria = pageData;
            }),
            this.personalizationService.getSummary().subscribe(summary => this.detailSummary = summary),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.showAiHelper = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
        );
    }


    private loadCurrentEntity(): Observable<[PersonalizationListApi, ResourceBodyApiModel, DistributionModel, PersonalizationTagEnum[]]> {
        let getEntityPersonalizations: Observable<PersonalizationListApi>;

        if (this.currentEntity.entityType === EntityType.CONTACT) {
            getEntityPersonalizations = this.distributionApi.getContactPersonalizations(this.distributionId, this.currentEntity.id);
        } else {
            getEntityPersonalizations = this.distributionApi.getOutletPersonalizations(this.distributionId, this.currentEntity.id);
        }

        return combineLatest([
            getEntityPersonalizations,
            from(this.distributionService.getDistributionHtml({hideViewInBrowserUrl: true})),
            this.distributionService.getFocusedResource(),
            this.personalizationService.getPersonalizationTags()
        ]).pipe(
            tap(([personalizationListApi, responseBody, distribution, tags]) => {
                this.loading = false;

                this.personalizations = personalizationListApi.personalizations
                    .filter(x => tags.includes(x.tagType))
                    .map(apiModel => new Personalization(apiModel));

                // b/e limitation - doesn't send empty personalizations for recipient view only
                // add in missing personalization fields
                const missingPesonalizationTags = tags.filter(tag => !this.personalizations.map(x => x.tagType).includes(tag));
                if (missingPesonalizationTags.length > 0) {
                    missingPesonalizationTags.forEach(tag => {
                        this.personalizations.push(new Personalization({
                            tagType: tag,
                            selections: [],
                        }));
                    });
                }

                this.personalizations.sort((a, b) => {
                    return this.personalizationOrder.indexOf(a.tagType) - this.personalizationOrder.indexOf(b.tagType);
                });

                this.distHeadingIndex = this.personalizations.findIndex(x => {
                    return x.tagType === PersonalizationTagEnum.INTRO || x.tagType === PersonalizationTagEnum.OUTRO;
                });

                if (this.personalizations.some(x => this.personalizationContactTypes.includes(x.tagType))) {
                    this.contactHeadingIndex = 0;
                }

                this.bodyHtml = responseBody.bodyHtml;
                this.previewHtml = distribution.previewText;
                this.subjectHtml = distribution.subjectLine;

                this.updateDistributionContent();

                if (this.currentEntity.entityType === EntityType.CONTACT) {
                    this.targetingRhpContextsService.setContact(this.currentEntity.id);
                } else {
                    this.targetingRhpContextsService.setOutlet(this.currentEntity.id);
                }

                this.updateSaveCount();
            }),
            catchError(() => {
                this.loading = false;

                const nameLabel = this.currentEntity.entityType === EntityType.CONTACT ?
                    `${(this.currentEntity as ContactModel).firstName} ${(this.currentEntity as ContactModel).lastName}` : this.currentEntity.name;

                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: 'Discover.distribution.personalizationDialog.loading.error.heading',
                    summary: this.localizationService.transform('Discover.distribution.personalizationDialog.loading.error.body', { name: nameLabel }),
                });

                return of(null);
            })
        );
    }

    tabChange(tabIndex: number) {
        this.selectedTab = tabIndex;
    }


    customChange(personalization: Personalization) {
        let apiCall: Observable<void>;

        const personalizationValue: PersonalizationValue = {
            tagType: personalization.tagType,
            value: personalization.custom
        };

        let nameLabel = '';
        if (this.currentEntity.entityType === EntityType.CONTACT) {
            nameLabel = `${(this.currentEntity as ContactModel).firstName} ${(this.currentEntity as ContactModel).lastName}`;
            apiCall = this.distributionApi.updateContactPersonalization(
                this.distributionId,
                this.currentEntity.id,
                personalizationValue
            );
        } else {
            nameLabel = this.currentEntity.name;
            apiCall = this.distributionApi.updateOutletPersonalization(
                this.distributionId,
                this.currentEntity.id,
                personalizationValue
            );
        }

        apiCall.pipe(
            catchError((error: HttpErrorResponse) => {
                personalization.custom = personalization.previousCustom;
                return this.personalizationService.handleUpsertError(error, personalizationValue, nameLabel);
            }),
        ).subscribe({
            next: () => {

                const previouslyCounted = personalization.previousCustom.length > 0;
                const currentlyCounted = personalization.custom.length > 0;

                const tagSummary = this.detailSummary.find(x => x.tagType === personalization.tagType);

                // update count if differential present
                if (tagSummary && !previouslyCounted && currentlyCounted) {
                    tagSummary.totalPersonalizations++;
                } else if (tagSummary && previouslyCounted && !currentlyCounted) {
                    tagSummary.totalPersonalizations--;
                }

                this.personalizationService.updateSummary(this.detailSummary);
                personalization.previousCustom = personalization.custom;

                this.updateSaveCount();
            }
        });

        this.updateDistributionContent();
    }

    switchToCustom(personalization: Personalization) {
        if (personalization.selectedSource === PersonalizationSourceTypeEnum.CUSTOM) {

            return;
        }

        personalization.selectedSource = PersonalizationSourceTypeEnum.CUSTOM;
        this.updateSelection(personalization);
    }


    updateSelection(personalization: Personalization) {
        const personalizationSelection: PersonalizationSelection = {
            tagType: personalization.tagType,
            selectionType: personalization.selectedSource,
        };


        if (this.currentEntity.entityType === EntityType.CONTACT) {
            this.distributionApi.upsertContactPersonalizationSelections(
                this.distributionId,
                this.currentEntity.id,
                personalizationSelection
            ).subscribe();
        } else {
            this.distributionApi.upsertOutletPersonalizationSelections(
                this.distributionId,
                this.currentEntity.id,
                personalizationSelection
            ).subscribe();
        }

        const previousSelection = personalization.selectedSource === PersonalizationSourceTypeEnum.CUSTOM ?
            PersonalizationSourceTypeEnum.SYSTEM : PersonalizationSourceTypeEnum.CUSTOM;

        let previouslyCounted: boolean;
        if (previousSelection === PersonalizationSourceTypeEnum.CUSTOM) {
            previouslyCounted = (personalization.custom.length > 0);
        } else {
            previouslyCounted = (personalization.system.length > 0);
        }

        let currentlyCounted: boolean;
        if (personalization.selectedSource === PersonalizationSourceTypeEnum.CUSTOM) {
            currentlyCounted = (personalization.custom.length > 0);
        } else {
            currentlyCounted = (personalization.system.length > 0);
        }

        const tagSummary = this.detailSummary.find(x => x.tagType === personalization.tagType);

        // update count if differential present
        if (!previouslyCounted && currentlyCounted) {
            tagSummary.totalPersonalizations++;
        } else if (previouslyCounted && !currentlyCounted) {
            tagSummary.totalPersonalizations--;
        }

        this.personalizationService.updateSummary(this.detailSummary);


        this.updateDistributionContent();
    }

    /** Save the custom value to the entity's profile privatedata */
    saveToProfile(personalization: Personalization) {

        let apiCall: Observable<string>;
        let nameLabel: string = '';
        const privateData: PatchPersonalization = {
            personalizationsToUpsert: [
                {
                    tagType: personalization.tagType,
                    value: personalization.custom
                }
            ]
        };

        if (this.currentEntity.entityType === EntityType.CONTACT) {
            nameLabel = `${(this.currentEntity as ContactModel).firstName} ${(this.currentEntity as ContactModel).lastName}`;
            apiCall = this.contactsPublicApi.updatePersonalizations(
                this.currentEntity.id,
                privateData
            );
        } else {
            nameLabel = this.currentEntity.name;
            apiCall = this.outletsPublicApi.updatePersonalizations(
                this.currentEntity.id,
                privateData
            );
        }

        apiCall.pipe(
            catchError((error: HttpErrorResponse) => this.personalizationService.handleUpsertError(
                error, privateData.personalizationsToUpsert[0], nameLabel
            )),
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    detail: 'Discover.distribution.personalizationSaved.heading',
                    summary: this.localizationService.transform('Discover.distribution.personalizationSaved.body', { entity: nameLabel }),
                });

                personalization.profile = personalization.custom;

                this.updateSaveCount();
            },
        });
    }

    saveAll() {

        let apiCall: Observable<string>;
        let count: number = 0;
        const privateData: PatchPersonalization = {
            personalizationsToUpsert: []
        };

        this.personalizations
            .filter(x => this.unsavedPeronsalizationTypes.includes(x.tagType))
            .forEach(personalization => {
                count++;
                privateData.personalizationsToUpsert.push({
                    tagType: personalization.tagType,
                    value: personalization.custom
                });
            });

        let nameLabel = '';
        if (this.currentEntity.entityType === EntityType.CONTACT) {
            nameLabel = `${(this.currentEntity as ContactModel).firstName} ${(this.currentEntity as ContactModel).lastName}`;
            apiCall = this.contactsPublicApi.updatePersonalizations(
                this.currentEntity.id,
                privateData
            ).pipe(
                catchError((error: HttpErrorResponse) => this.personalizationService.handleUpsertError(
                    error, privateData.personalizationsToUpsert[0], nameLabel
                )),
            );
        } else {
            nameLabel = this.currentEntity.name;
            apiCall = this.outletsPublicApi.updatePersonalizations(
                this.currentEntity.id,
                privateData
            ).pipe(
                catchError((error: HttpErrorResponse) => this.personalizationService.handleUpsertError(
                    error, privateData.personalizationsToUpsert[0], nameLabel
                )),
            );
        }

        apiCall.subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    detail: 'Discover.distribution.personalizationSaveAll.heading',
                    summary: this.localizationService.transform('Discover.distribution.personalizationSaveAll.body', { count: count }),
                });

                this.personalizations
                    .filter(x => this.unsavedPeronsalizationTypes.includes(x.tagType))
                    .forEach(personalization => {
                        personalization.profile = personalization.custom;
                    });

                this.updateSaveCount();

            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: 'Discover.distribution.personalizationSaveAll.error.heading',
                });
            }
        });
    }

    editFallback() {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
            }
        };
        this.coreDialogService.open(FallbackPersonalizationsDialogComponent, io);
    }

    aiRefinement(
        personalization: Personalization,
        recipient: ContactModel | OutletModel
    ) {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                personalizationType: personalization.tagType,
                distributionId: this.distributionId,
                aiEnabled: this.showAiHelper,
                userText: personalization.custom,
                recipient: recipient
            },
            outputs: {
                contentChanges: (content: string) => {
                    personalization.custom = content;
                    this.switchToCustom(personalization);
                    this.customChange(personalization);
                }
            }
        };

        this.coreDialogService.open(PersonalizationWorkbenchDialogComponent, io);
    }

    openPreview(
        text: string,
        tagType: PersonalizationTagEnum.INTRO | PersonalizationTagEnum.OUTRO,
    ) {
        let nameLabel: string = '';
        if (this.currentEntity.entityType === EntityType.CONTACT) {
            nameLabel = `${(this.currentEntity as ContactModel).firstName} ${(this.currentEntity as ContactModel).lastName}`;
        } else {
            nameLabel = this.currentEntity.name;
        }

        const personalizedType = this.localizationService.transform(`Discover.personalizationTag.${tagType}`);

        const io = {
            inputs: {
                content: text,
                heading: this.localizationService.transform('Discover.distribution.personalizationPreview', { name: nameLabel, type: personalizedType })
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(PreviewTextDialogComponent, io);
    }

    previous() {
        const currentIndex = this.recipients.findIndex(x => x.id === this.currentEntity.id);

        if (currentIndex > 0) {
            this.loading = true;
            this.currentIndex = currentIndex - 1;
            this.currentEntity = this.recipients[this.currentIndex];
            this.loadCurrentEntity().subscribe();
        } else {
            // decrement page and trigger search
            if (this.pageCriteria.page > 0) {
                this.loading = true;
                this.currentEntity = null;
                this.setPageBeginning = false;
                this.pageCriteria.page--;
                this.personalizationService.setPageCriteria(this.pageCriteria);
            }
        }
    }

    next() {
        const currentIndex = this.recipients.findIndex(x => x.id === this.currentEntity.id);

        if (currentIndex < this.recipients.length - 1) {
            this.loading = true;
            this.currentIndex = currentIndex + 1;
            this.currentEntity = this.recipients[this.currentIndex];
            this.loadCurrentEntity().subscribe();
        } else {
            // increment page and trigger search
            if (this.pageCriteria.page < this.personalizationService.totalNumberOfPages) {
                this.loading = true;
                this.currentEntity = null;
                this.setPageBeginning = true;
                this.pageCriteria.page++;
                this.personalizationService.setPageCriteria(this.pageCriteria);
            }
        }
    }

    toggleHighlights() {
        this.highlightTags = !this.highlightTags;
    }

    close() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }


    private updateSaveCount() {
        this.unsavedPeronsalizationTypes = [];
        this.unsavedPeronsalizations = this.personalizations.reduce(
            (total, personalization) => {
                const showSave = (
                    personalization.custom !== personalization.profile &&
                    (
                        (this.currentEntity.entityType === EntityType.CONTACT &&
                        this.allowedContactProfileSaveTags.includes(personalization.tagType)) ||
                        (this.currentEntity.entityType === EntityType.OUTLET &&
                        this.allowedOutletProfileSaveTags.includes(personalization.tagType))
                    )
                );

                if (showSave) {
                    this.unsavedPeronsalizationTypes.push(personalization.tagType);
                }

                return total + (showSave ? 1 : 0);
            }, 0
        );
    }


    private generateTagMap(personalizations: Personalization[]): Map<string, string> {
        const tagMap = new Map<string, string>();
        personalizations.forEach(personalization => {
            let value = '';

            switch(personalization.selectedSource) {
                case PersonalizationSourceTypeEnum.SYSTEM: {
                    if (personalization.system.length > 0) {
                        value = personalization.system;
                    } else {
                        value = personalization.fallback;
                    }
                    break;
                }
                case PersonalizationSourceTypeEnum.CUSTOM: {
                    value = personalization.custom;
                    break;
                }
            }

            tagMap.set(`[%${personalization.tagType}%]`, value);
        });

        return tagMap;
    }

    /**
     * Finds all personalization tags and replaces them with coressponding tagMap value
     */
    private replaceTags(text: string, showHighlight: boolean, tagMap: Map<string, string>): string {
        if (!text) {
            return null;
        }

        // escpase merge tags
        const regex = new RegExp(
            Array.from(tagMap.keys())
                .map(x => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
                .join('|'), 'g');

        const cleanedText = text.replace(regex, (match) => {
            const replacement = tagMap.get(match);

            if (replacement !== undefined) {
                if (match === `[%${PersonalizationTagEnum.INTRO}%]` ||  match === `[%${PersonalizationTagEnum.OUTRO}%]`) {
                    return showHighlight ? `<div class="highlight"> ${replacement} </div>` : replacement;
                } else {
                    return showHighlight ? `<span class="highlightInline"> ${replacement} </span>` : replacement;
                }
            } else {
                return match;
            }
        });

        return cleanedText;
    }

    private updateDistributionContent() {
        const tagMap: Map<string, string> = this.generateTagMap(this.personalizations);

        this.body = this.replaceTags(this.bodyHtml, false, tagMap);
        this.bodyHighlight = this.replaceTags(this.bodyHtml, true, tagMap);

        this.subject = this.replaceTags(this.subjectHtml, false, tagMap);
        this.subjectHighlight = this.replaceTags(this.subjectHtml, true, tagMap);

        this.preview = this.replaceTags(this.previewHtml, false, tagMap);
        this.previewHighlight = this.replaceTags(this.previewHtml, true, tagMap);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
